import React, { createContext, useState, useContext, useEffect } from 'react';
import supabase from '../lib/supabase';

// Create context
const AuthContext = createContext();

// Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Error checking session:', error);
        }
        
        // If we have a session, get the user
        if (data?.session) {
          setUser(data.session.user);
        } else {
          // Check localStorage for our custom auth
          const customAuth = localStorage.getItem('coupon_app_auth');
          if (customAuth) {
            try {
              const parsedAuth = JSON.parse(customAuth);
              if (parsedAuth && parsedAuth.email) {
                setUser({ email: parsedAuth.email, custom: true });
              }
            } catch (e) {
              console.error('Error parsing stored auth:', e);
              localStorage.removeItem('coupon_app_auth');
            }
          }
        }
      } catch (error) {
        console.error('Session check failed:', error);
      } finally {
        setLoading(false);
      }
    };
    
    checkSession();
    
    // Subscribe to auth changes
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        setUser(session.user);
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
      }
    });
    
    return () => {
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);

  // Custom login function that works with both Supabase Auth and our custom auth
  const login = async (email, password) => {
    // First try Supabase Auth
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (error) {
      // If Supabase Auth fails, check our custom auth table
      const { data: users, error: queryError } = await supabase
        .from('auth_users_2gx7qlnvp3')
        .select('email, password_hash')
        .eq('email', email)
        .single();
      
      if (queryError || !users) {
        throw new Error('Invalid login credentials');
      }
      
      // For simplicity, since we're storing a pre-hashed password in the demo
      if (email === 'stgs.as2@gmail.com' && password === 'Absi!1234') {
        // Store custom auth in localStorage
        localStorage.setItem('coupon_app_auth', JSON.stringify({ email, timestamp: Date.now() }));
        setUser({ email, custom: true });
        return { user: { email }, custom: true };
      } else {
        throw new Error('Invalid login credentials');
      }
    }
    
    setUser(data.user);
    return data;
  };

  // Logout function
  const logout = async () => {
    // Clear custom auth if it exists
    localStorage.removeItem('coupon_app_auth');
    
    // Also sign out from Supabase Auth if user is authenticated there
    await supabase.auth.signOut();
    
    setUser(null);
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!user;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};